import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { currentRevisionOf } from '@kronos/domain'
import { repository } from '../data'
import type { ComponentType, Revision } from '../data'
import { EmptyState, Panel } from './primitives'
import { StructureTree } from './tree/StructureTree'
import { TreeIcon } from './tree/treeIcons'

/**
 * Design › How it's made — §11.2's "the BOM as a tree, with quantity lines
 * under each assembly", for any item that is made of other items.
 *
 * This is the **as-designed** tree: what a robot is supposed to be, answerable
 * the day the item master is typed in and long before there is a robot to
 * walk. The as-built counterpart — what is fitted to R-07 right now — comes off
 * open installations and belongs on that robot's own page.
 *
 * This file is only the chooser: which item to draw, and how many to plan for.
 * The drawing is `StructureTree`, which an item's own page embeds too.
 */
export function DesignTree(): JSX.Element {
  const [rootId, setRootId] = useState<string | null>(null)
  const [quantity, setQuantity] = useState(1)

  const types = useQuery({ queryKey: ['component-types'], queryFn: () => repository.componentTypes() })
  const revisions = useQuery({ queryKey: ['revisions'], queryFn: () => repository.revisions() })

  if (types.isPending || revisions.isPending) return <span className="muted">Loading…</span>

  const assembled = drawable(types.data ?? [], revisions.data ?? [])
  if (assembled.length === 0) return <NothingToDraw types={types.data?.length ?? 0} />
  const chosen = assembled.find((t) => t.id === rootId) ?? assembled[0]
  if (chosen === undefined) return <NothingToDraw types={0} />

  return (
    <div className="stack">
      <div className="toolbar st-chooser">
        <label className="st-chooser__field">
          <span className="st-chooser__label">Show how this is made</span>
          <select value={chosen.id} onChange={(e) => setRootId(e.target.value)} className="st-picker">
            {assembled.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
                {t.isRoot ? ' — robot' : ''}
              </option>
            ))}
          </select>
        </label>
        <div className="st-chooser__field">
          <span className="st-chooser__label" id="st-qty-label">
            Plan for
          </span>
          <div className="st-stepper" role="group" aria-labelledby="st-qty-label">
            <button type="button" aria-label="One fewer" disabled={quantity <= 1} onClick={() => setQuantity((q) => Math.max(1, q - 1))}>
              <TreeIcon name="minus" />
            </button>
            <output aria-live="polite">{quantity}</output>
            <button type="button" aria-label="One more" disabled={quantity >= 999} onClick={() => setQuantity((q) => Math.min(999, q + 1))}>
              <TreeIcon name="plus" />
            </button>
          </div>
        </div>
      </div>

      <StructureTree
        rootTypeId={chosen.id}
        quantity={quantity}
        onReroot={(id) => {
          setRootId(id)
          setQuantity(1)
        }}
      />
    </div>
  )
}

/**
 * Items worth drawing — ones whose current revision lists something — robots
 * first, because the robot is the tree everybody means by "the tree"; then the
 * rest by name. A picker over an almost-empty canvas is how a working feature
 * gets reported broken.
 */
function drawable(types: readonly ComponentType[], revisions: readonly Revision[]): ComponentType[] {
  return types
    .filter((t) => (currentRevisionOf(revisions, t.id)?.lines.length ?? 0) > 0)
    .sort((a, b) => Number(b.isRoot) - Number(a.isRoot) || a.name.localeCompare(b.name))
}

function NothingToDraw({ types }: { types: number }): JSX.Element {
  return (
    <Panel>
      <EmptyState icon="catalog" title="Nothing is made of anything yet">
        A tree needs at least one item that is <em>made of</em> other items.{' '}
        {types === 0
          ? 'Nothing is defined at all yet — start with what you buy, under Inventory › New item.'
          : 'The items are there, but none of them has a parts list yet.'}{' '}
        Open an item you assemble and add what it is made of; the tree is drawn from those lines and from nothing else.
      </EmptyState>
    </Panel>
  )
}
